// Tamaño del popup del chat flotante: PROPORCIONES del viewport, no píxeles.
//
// 🔴 EL PROBLEMA que resuelve: 400×620 px fijos. En un monitor 3K (2880×1620)
// eso es 14 % del ancho por 38 % del alto → el chat se ve diminuto; en 1024×768
// el MISMO popup ocupa casi toda la ventana. Un número fijo no puede servir a
// las dos pantallas.
//
// ⛔ LA INVARIANTE QUE NO SE TOCA (fix 087f02cf): el popup NUNCA puede ser más
// alto/ancho que el viewport, porque está anclado abajo y el header —el único
// lugar desde donde se cierra el chat— se iría de pantalla. Por eso los PISOS se
// acotan contra los TECHOS: en una ventana muy baja (teléfono acostado, 667×375)
// MIN_H > maxH, y un `clamp(v, lo, hi)` con lo > hi devuelve el PISO, es decir un
// popup más alto que la pantalla. Está cubierto por popupSize.test.mjs.
//
// Vive en su propio archivo (y no dentro del componente) para que el test pueda
// evaluarlo sin arrastrar React ni MUI.

export const DEFAULT_W_RATIO = 0.21;
export const DEFAULT_H_RATIO = 0.72;
export const MAX_W_RATIO = 0.45;
export const MAX_H_RATIO = 0.92;

// Piso del DEFAULT: los valores históricos. En pantallas chicas la proporción da
// menos que esto, y achicar el popup ahí sería una regresión. Así, ≤1366 px de
// ancho queda exactamente como estaba y sólo las pantallas grandes crecen.
export const BASE_W = 400;
export const BASE_H = 620;

// Pisos del RESIZE manual (el usuario sí puede bajar del default).
export const MIN_W = 340;
export const MIN_H = 400;

// Único techo absoluto que sobrevive, y sólo para el ANCHO: una columna de
// burbujas más ancha que esto se lee mal (largo de línea). El ALTO no tiene ese
// límite, así que es 100 % relativo — se acabó el techo de 860 px.
export const MAX_W_ABS = 720;

// Margen mínimo entre el popup y el borde de arriba de la pantalla.
export const VIEWPORT_GAP = 8;

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// Techos y pisos para UN viewport dado.
export const limitsFor = (vw, vh) => {
  const maxW = Math.min(
    MAX_W_ABS,
    Math.round(vw * MAX_W_RATIO),
    Math.max(1, vw - VIEWPORT_GAP)
  );
  const maxH = Math.min(
    Math.round(vh * MAX_H_RATIO),
    Math.max(1, vh - VIEWPORT_GAP)
  );
  return { minW: Math.min(MIN_W, maxW), minH: Math.min(MIN_H, maxH), maxW, maxH };
};

// Tamaño por defecto (sin preferencia guardada): proporción del viewport, nunca
// por debajo del histórico ni por encima del techo.
export const defaultSizeFor = (vw, vh) => {
  const L = limitsFor(vw, vh);
  return {
    w: clamp(Math.round(vw * DEFAULT_W_RATIO), Math.min(BASE_W, L.maxW), L.maxW),
    h: clamp(Math.round(vh * DEFAULT_H_RATIO), Math.min(BASE_H, L.maxH), L.maxH),
  };
};

// Tamaño efectivo: el pedido (preferencia guardada o default) acotado al
// viewport actual. Un alto elegido en el monitor 3K no puede desbordar la
// notebook con la que se abre después.
export const effectiveSize = (requested, vw, vh) => {
  const L = limitsFor(vw, vh);
  const d = defaultSizeFor(vw, vh);
  return {
    w: clamp(Number(requested?.w) || d.w, L.minW, L.maxW),
    h: clamp(Number(requested?.h) || d.h, L.minH, L.maxH),
  };
};
